import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Brain, Check, Clock, X } from "lucide-react";
import {
  useGetInteractiveMaterial,
  useGetTeacherAssignedInteractiveMaterials,
} from "@packages/api/madlenAPI";
import {
  StudentInteractiveMaterialResponseDto,
  TestQuestion,
  InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem,
  InteractiveMaterialResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "@core/index";
import LoadingContainer from "@packages/components/Loading";

const calculateScore = (
  student: StudentInteractiveMaterialResponseDto,
  material: InteractiveMaterialResponseDto,
) => {
  const allAnswers = [...student.quizAnswers, ...student.videoQuizAnswers];
  if (allAnswers.length === 0) return 0;

  const totalScore = allAnswers.reduce(
    (acc, answer) => acc + (answer.score || 0),
    0,
  );

  const totalQuestions =
    (material.generatedQuizQuestions?.length || 0) +
    (material.generatedVideoQuestions?.length || 0);

  return Math.round((totalScore / totalQuestions) * 100);
};

const calculateProgress = (
  student: StudentInteractiveMaterialResponseDto,
  material: InteractiveMaterialResponseDto,
) => {
  const totalQuestions =
    (material.generatedQuizQuestions?.length || 0) +
    (material.generatedVideoQuestions?.length || 0);

  if (totalQuestions === 0) return 0;

  const answeredQuestions =
    student.quizAnswers.length + student.videoQuizAnswers.length;
  return Math.round((answeredQuestions / totalQuestions) * 100);
};

const StudentAnswersView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id, studentId } = useParams<{ id: string; studentId: string }>();

  const {
    data: material,
    isLoading: isLoadingMaterial,
    error: materialError,
  } = useGetInteractiveMaterial(Number(id));

  const {
    data: studentMaterials,
    isLoading: isLoadingStudents,
    error: studentsError,
  } = useGetTeacherAssignedInteractiveMaterials();

  const student = useMemo(() => {
    if (!studentMaterials) return null;
    return studentMaterials.find(
      (sm) =>
        sm.interactiveMaterialId === Number(id) &&
        sm.studentId === Number(studentId),
    );
  }, [studentMaterials, id, studentId]);

  const quizTestQuestions = useMemo(() => {
    return material?.generatedQuizQuestions?.filter(
      (question) => question.type === "TEST",
    );
  }, [material]);

  const quizTFQuestions = useMemo(() => {
    return material?.generatedQuizQuestions?.filter(
      (question) => question.type === "TF",
    );
  }, [material]);

  const videoTestQuestions = useMemo(() => {
    return material?.generatedVideoQuestions?.filter(
      (question) =>
        question.type === "TEST" &&
        Object.keys((question as any).options ?? {})?.length !== 2,
    );
  }, [material]);

  const videoTFQuestions = useMemo(() => {
    return material?.generatedVideoQuestions?.filter(
      (question) =>
        question.type === "TF" ||
        Object.keys((question as any).options ?? {})?.length === 2,
    );
  }, [material]);

  if (isLoadingMaterial || isLoadingStudents) {
    return (
      <LoadingContainer
        title={t("interactive_material.loading.title")}
        themeColor="rose"
      />
    );
  }

  if (materialError || studentsError || !material || !student) {
    return (
      <div className="text-center text-gray-600 dark:text-gray-300">
        {t("interactive_material.error.loadingError")}
      </div>
    );
  }

  const renderQuestionWithAnswer = (
    question: InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem,
    studentAnswer: any | null,
    type: "quiz" | "video",
  ) => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                  type === "quiz"
                    ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                    : "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
                }`}
              >
                {type === "quiz"
                  ? t("interactive_material.questions.title")
                  : t("interactive_material.questions.videoTitle")}
              </div>
            </div>
            <div
              className={`flex items-center gap-2 ${!studentAnswer ? "text-gray-500 dark:text-gray-400" : studentAnswer.isCorrect ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
            >
              {!studentAnswer ? (
                <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700">
                  {t("interactive_material.review.notAnswered")}
                </div>
              ) : studentAnswer.isCorrect ? (
                <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-700">
                  {t("common.true")}
                  <Check size={20} />
                </div>
              ) : (
                <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-700">
                  {t("common.false")}
                  <X size={20} />
                </div>
              )}
            </div>
          </div>

          <div>
            <Text variant={"p"} weight={"medium"}>
              {question.body}
            </Text>
            {question.explanation && (
              <Text variant={"p"} status="secondary" className="mt-2">
                {question.explanation}
              </Text>
            )}
          </div>

          {question.type === "TEST" && (question as TestQuestion).options && (
            <div className="grid grid-cols-1 gap-2">
              {Object.entries((question as TestQuestion).options!).map(
                ([key, value]) => {
                  const isStudentAnswer =
                    studentAnswer &&
                    (studentAnswer.studentAnswer === key ||
                      studentAnswer.studentAnswer === value ||
                      (typeof studentAnswer.studentAnswer === "number" &&
                        studentAnswer.studentAnswer.toString() === key));

                  const isCorrectAnswer =
                    (question as TestQuestion).answer === key;
                  return (
                    <div
                      key={key}
                      className={`p-3 rounded-lg border ${
                        isStudentAnswer
                          ? studentAnswer.isCorrect
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : isCorrectAnswer
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{key}</span>
                          <span>{value}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isStudentAnswer && (
                            <div
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                studentAnswer.isCorrect
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              }`}
                            >
                              {!studentAnswer.isCorrect &&
                                t("interactive_material.review.studentAnswer")}
                              {studentAnswer.isCorrect ? (
                                <Check size={12} />
                              ) : (
                                <X size={12} />
                              )}
                            </div>
                          )}
                          {isCorrectAnswer && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              {!(isStudentAnswer && studentAnswer.isCorrect) &&
                                t("interactive_material.review.correctAnswer")}
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}

          {question.type === "TF" && (
            <div className="mt-4 space-y-4">
              <div className="flex gap-4">
                {[true, false].map((value) => {
                  let booleanAnswer = null;
                  if (studentAnswer) {
                    booleanAnswer =
                      studentAnswer.studentAnswer?.toString().toLowerCase() ===
                        "doğru" ||
                      studentAnswer.studentAnswer?.toString().toLowerCase() ===
                        "true";
                  }
                  const isStudentAnswer =
                    studentAnswer && booleanAnswer === value;
                  const isCorrectAnswer = question.answer === value;

                  return (
                    <div
                      key={value.toString()}
                      className={`flex-1 p-3 rounded-lg border ${
                        isStudentAnswer
                          ? studentAnswer.isCorrect
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : isCorrectAnswer
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{t(`common.${value}`)}</span>
                        <div className="flex items-center gap-2">
                          {isStudentAnswer && (
                            <div
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                studentAnswer.isCorrect
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              }`}
                            >
                              {!studentAnswer.isCorrect &&
                                t("interactive_material.review.studentAnswer")}
                              {studentAnswer.isCorrect ? (
                                <Check size={12} />
                              ) : (
                                <X size={12} />
                              )}
                            </div>
                          )}
                          {isCorrectAnswer && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              {!(isStudentAnswer && studentAnswer.isCorrect) &&
                                t("interactive_material.review.correctAnswer")}
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/content/interactive-materials/${id}`)}
              textColor="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              size="icon"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <Text variant={"h1"} size={"3xl"} className="mb-2">
                {student.studentName}
              </Text>
              <Text variant={"p"} status="secondary" size={"base"}>
                {material?.title ||
                  material?.youtubeTitle ||
                  "quickActions.interactiveMaterial"}
              </Text>
            </div>
          </div>

          <div className="flex-1 flex items-center gap-6 mt-4 md:mt-0 ml-12 mr-2">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                  <Brain className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <Text className="text-lg font-medium text-gray-600 dark:text-gray-300">
                  {t("interactive_material.review.metrics.score")}
                </Text>
              </div>
              <Text variant={"h2"} className="mt-1" size={"2xl"}>
                {calculateScore(student, material)}%
              </Text>
            </div>

            <div className="flex flex-1 flex-col w-36 md:w-48 ml-8">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                  <Clock className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <Text className="text-lg font-medium text-gray-600 dark:text-gray-300">
                  {t("common.progress")}
                </Text>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-full bg-gray-200 dark:bg-gray-700 mt-2 rounded-full h-2">
                  <div
                    className="bg-rose-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${calculateProgress(student, material)}%`,
                    }}
                  />
                </div>
                <Text
                  variant="span"
                  status="subtle"
                  weight={"medium"}
                  className="min-w-[40px] text-right mt-1"
                >
                  {calculateProgress(student, material)}%
                </Text>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quizTestQuestions && quizTestQuestions.length > 0 && (
            <span className="text-gray-600 dark:text-gray-300 text-xl font-semibold col-span-2">
              {t("interactive_material.questions.title")} ({t("common.test")})
            </span>
          )}
          {quizTestQuestions?.map((question, index) => {
            const studentAnswer = student.quizAnswers.find(
              (answer) => answer.subcontentId === question.subcontentId,
            );
            return (
              <div key={index}>
                {renderQuestionWithAnswer(
                  question,
                  studentAnswer || null,
                  "quiz",
                )}
              </div>
            );
          })}
          {quizTFQuestions && quizTFQuestions.length > 0 && (
            <span className="text-gray-600 dark:text-gray-300 text-xl font-semibold col-span-2">
              {t("interactive_material.questions.title")} ({t("common.tf")})
            </span>
          )}
          {quizTFQuestions?.map((question, index) => {
            const studentAnswer = student.quizAnswers.find(
              (answer) => answer.subcontentId === question.subcontentId,
            );
            return (
              <div key={index}>
                {renderQuestionWithAnswer(
                  question,
                  studentAnswer || null,
                  "quiz",
                )}
              </div>
            );
          })}

          {videoTestQuestions && videoTestQuestions.length > 0 && (
            <span className="text-gray-600 dark:text-gray-300 text-xl font-semibold col-span-2">
              {t("interactive_material.questions.videoTitle")} (
              {t("common.test")})
            </span>
          )}

          {videoTestQuestions?.map((question, index) => {
            const studentAnswer = student.videoQuizAnswers.find(
              (answer) => answer.subcontentId === question.subcontentId,
            );
            return (
              <div key={index}>
                {renderQuestionWithAnswer(
                  question,
                  studentAnswer || null,
                  "video",
                )}
              </div>
            );
          })}

          {videoTFQuestions && videoTFQuestions.length > 0 && (
            <span className="text-gray-600 dark:text-gray-300 text-xl font-semibold col-span-2">
              {t("interactive_material.questions.videoTitle")} ({t("common.tf")}
              )
            </span>
          )}

          {videoTFQuestions?.map((question, index) => {
            const studentAnswer = student.videoQuizAnswers.find(
              (answer) => answer.subcontentId === question.subcontentId,
            );
            return (
              <div key={index}>
                {renderQuestionWithAnswer(
                  question,
                  studentAnswer || null,
                  "video",
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentAnswersView;
